import { jsx as _jsx } from "react/jsx-runtime";
import { useCallback, useEffect, useLayoutEffect, useMemo, } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { initState as resetDatasets } from 'Datasets/store';
import useRouter from 'Router/hooks/useRouter/useRouter';
import { PhenotypeViewRoute, } from 'Router/index';
import { useUtag } from 'Shared/hooks';
import { getViewUtagData } from 'Shared/utils';
import { ELevelResult } from 'Shared/types';
import { encodeStringToBase64, isBase64String, isValidUuid } from 'Common/utils';
import { useWorker } from 'Common/hooks';
import { EPageName } from 'Common/types';
import { useValidatedPhenotypeId } from 'PhenotypeView/hooks';
import { getCommonErrorType, getPhenotype } from 'PhenotypeView/store/selectors';
import { createWorker } from 'PhenotypeView/worker/createWorker';
import { cleanup, initWorker, terminateWorker, updateFilteredAssociations, } from 'PhenotypeView/store/slice';
import { fetchAPIData, setDataExists } from 'PhenotypeView/store/actions';
import { ECleanup } from 'PhenotypeView/store/types';
import { TEALIUM_EVENT } from 'Types/analytics';
import Page from './Page';
export const PAGE_NAME = EPageName.Phenotype;
const TRACKING_EVENT = {
    [ELevelResult.Gene]: TEALIUM_EVENT.PHENOTYPE_VIEW_GLR,
    [ELevelResult.Variant]: TEALIUM_EVENT.PHENOTYPE_VIEW_VLR,
    [ELevelResult.Phenotype]: TEALIUM_EVENT.PHENOTYPE_VIEW_PLR,
};
const PageContainer = () => {
    const dispatch = useDispatch();
    const utag = useUtag();
    useWorker(createWorker, initWorker, terminateWorker, updateFilteredAssociations);
    const { getParams, navigateTo, redirectTo, getLocation, useMatch, } = useRouter();
    const { pathname } = getLocation();
    const { encodedPhenotypeName: phenotypeNameParam, datasetVersionId, levelResult, } = getParams();
    const errorType = useSelector(getCommonErrorType);
    const phenotype = useSelector(getPhenotype);
    const encodedPhenotypeName = useMemo(() => (isBase64String(phenotypeNameParam) || isValidUuid(phenotypeNameParam)
        ? phenotypeNameParam
        : encodeStringToBase64(phenotypeNameParam)), [phenotypeNameParam]);
    const validatedPhenotypeId = useValidatedPhenotypeId();
    const isRoutesMatch = !!useMatch(PhenotypeViewRoute.getPath({
        phenotypeId: validatedPhenotypeId,
        encodedPhenotypeName,
        datasetVersionId,
    }));
    const handleDatasetChange = useCallback((newDatasetVersionId) => {
        navigateTo(PhenotypeViewRoute, {
            datasetVersionId: newDatasetVersionId,
            phenotypeId: validatedPhenotypeId,
            encodedPhenotypeName,
            levelResult,
        }, undefined, { phenotypeId: validatedPhenotypeId });
    }, [
        levelResult,
        validatedPhenotypeId,
        encodedPhenotypeName,
        navigateTo,
    ]);
    const referrerState = useMemo(() => ({
        referrerURI: pathname,
        referrerData: {
            name: `${PAGE_NAME.toLowerCase()} '${phenotype.name}'`,
        },
    }), [pathname, phenotype.name]);
    useLayoutEffect(() => {
        const fetchAction = dispatch(fetchAPIData({
            phenotypeId: validatedPhenotypeId,
            encodedPhenotypeName,
            datasetVersionId,
        }));
        return () => {
            fetchAction.abort();
            dispatch(cleanup(ECleanup.All));
        };
        /* eslint-disable-next-line react-hooks/exhaustive-deps */
    }, [dispatch, encodedPhenotypeName, datasetVersionId]);
    /**
     * Error handling
     */
    useEffect(() => {
        if (errorType === 0 /* NotFound */) {
            redirectTo(1 /* NotFound */, {});
        }
        if (errorType === 5 /* ServerError */ || errorType === 3 /* Unknown */) {
            redirectTo(7 /* ServerError */, {});
        }
    }, [errorType, dispatch, redirectTo]);
    /**
     * Datasets and chromosomes data cleanup before route changes
     *
     * Triggered when user navigates to any other view
     */
    useEffect(() => () => {
        dispatch(resetDatasets());
    }, [dispatch]);
    /**
     * Consider the data might not exist for a new resource
     */
    useEffect(() => () => {
        dispatch(setDataExists(false));
        /* eslint-disable-next-line react-hooks/exhaustive-deps */
    }, [validatedPhenotypeId]);
    useEffect(() => {
        if (levelResult) {
            utag.view(getViewUtagData({ tealiumEvent: TRACKING_EVENT[levelResult] }));
        }
    }, [levelResult, utag]);
    return (_jsx(Page, { levelResult: levelResult, isRoutesMatch: isRoutesMatch, onDatasetChange: handleDatasetChange, referrerState: referrerState, pageName: PAGE_NAME, phenotypeName: phenotype.name, phenotypeId: phenotype.id }, void 0));
};
export default PageContainer;
